/**
 * HeatmapGrid — shared GitHub-style heatmap grid used by both
 * CompletionHeatmap (all tasks, count-based) and RecurrenceHistory (single task, outcome-based).
 *
 * Handles: responsive week calculation, day labels, cell rendering, today ring, hover.
 * Consumers provide the data and choose which legend to show via props.
 */

import { useRef, useState, useEffect, useCallback, useMemo } from "react"
import { cn } from "@/shared/utils/general/cn"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HeatmapCell {
  /** "YYYY-MM-DD" or "" for empty placeholder */
  date: string
  /** CSS class(es) for the cell background color */
  colorClass: string
  /** Tooltip text on hover */
  tooltip: string
  /** Arbitrary data passed through for hover callbacks */
  data?: unknown
}

export interface HeatmapGridProps {
  /** Grid data: weeks × 7 days. Built by the consumer. */
  grid: HeatmapCell[][]
  /** Called when responsive sizing determines actual weeks needed */
  onWeeksChange?: (weeks: number) => void
  /** Today's date string "YYYY-MM-DD" — cell gets a ring highlight */
  today?: string
  /** Show hover interaction (scale + ring) */
  interactive?: boolean
  /** Called when a cell is hovered */
  onCellHover?: (cell: HeatmapCell | null) => void
  /** Legend content rendered below the grid */
  legend?: React.ReactNode
  className?: string
  /** Min weeks for responsive sizing */
  minWeeks?: number
  /** Max weeks for responsive sizing */
  maxWeeks?: number
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DAY_LABELS = ["M", "", "W", "", "F", "", "S"]
const CELL_SIZE = 11
const CELL_GAP = 2
const COL_WIDTH = CELL_SIZE + CELL_GAP
const LABEL_WIDTH = 16
const DEFAULT_MIN_WEEKS = 4
const DEFAULT_MAX_WEEKS = 200

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HeatmapGrid({
  grid,
  onWeeksChange,
  today,
  interactive = false,
  onCellHover,
  legend,
  className,
  minWeeks = DEFAULT_MIN_WEEKS,
  maxWeeks = DEFAULT_MAX_WEEKS,
}: HeatmapGridProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [hoveredDate, setHoveredDate] = useState<string | null>(null)

  // Responsive: calculate weeks that fit
  const updateWeeks = useCallback(() => {
    if (!onWeeksChange) return
    const el = containerRef.current
    if (!el) return
    const available = el.clientWidth - LABEL_WIDTH
    const fits = Math.floor(available / COL_WIDTH)
    onWeeksChange(Math.max(minWeeks, Math.min(maxWeeks, fits)))
  }, [onWeeksChange, minWeeks, maxWeeks])

  useEffect(() => {
    if (!onWeeksChange) return
    const el = containerRef.current
    if (!el) return
    updateWeeks()
    const observer = new ResizeObserver(updateWeeks)
    observer.observe(el)
    return () => observer.disconnect()
  }, [updateWeeks, onWeeksChange])

  // Compute grid width so parent can match it
  const gridWidth = useMemo(
    () => LABEL_WIDTH + grid.length * COL_WIDTH,
    [grid.length],
  )

  return (
    <div ref={containerRef} className={cn("min-w-0", className)}>
      <div className="flex gap-0.5" style={{ width: gridWidth }}>
        {/* Day labels */}
        <div className="mr-1 flex flex-col gap-0.5">
          {DAY_LABELS.map((label, i) => (
            <div
              key={i}
              className="flex h-[11px] w-3 items-center justify-center text-[8px] text-muted-foreground"
            >
              {label}
            </div>
          ))}
        </div>

        {/* Weeks */}
        {grid.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-0.5">
            {week.map((cell, di) => {
              if (!cell.date) {
                return <div key={di} className="h-[11px] w-[11px]" />
              }
              const isToday = cell.date === today
              const isHovered = interactive && hoveredDate === cell.date
              return (
                <div
                  key={di}
                  title={!interactive ? cell.tooltip : undefined}
                  className={cn(
                    "h-[11px] w-[11px] rounded-[2px]",
                    cell.colorClass,
                    interactive && "cursor-pointer transition-all",
                    isToday && "ring-1 ring-foreground/20 ring-offset-1 ring-offset-background",
                    isHovered && "ring-1 ring-foreground/40 ring-offset-1 ring-offset-background scale-125",
                  )}
                  onMouseEnter={
                    interactive
                      ? () => {
                          setHoveredDate(cell.date)
                          onCellHover?.(cell)
                        }
                      : undefined
                  }
                  onMouseLeave={
                    interactive
                      ? () => {
                          setHoveredDate(null)
                          onCellHover?.(null)
                        }
                      : undefined
                  }
                />
              )
            })}
          </div>
        ))}
      </div>

      {/* Legend — provided by consumer */}
      {legend && <div className="mt-1 flex h-4 items-center text-[9px] text-muted-foreground">{legend}</div>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

export { CELL_SIZE, CELL_GAP, COL_WIDTH, LABEL_WIDTH }

const SHORT_WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export function formatHeatmapDate(dateStr: string): string {
  const parts = dateStr.split("-")
  const y = Number(parts[0])
  const m = Number(parts[1])
  const d = Number(parts[2])
  const date = new Date(y, m - 1, d)
  return `${SHORT_WEEKDAYS[date.getDay()]}, ${SHORT_MONTHS[date.getMonth()]} ${d}`
}
